/** User administration (§4.2.1). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, UserCheck, UserX } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { dateTime } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface UserRow {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'ACTIVE' | 'DISABLED';
  lastLoginAt: string | null;
  mustResetPassword: boolean;
  lockedUntil: string | null;
}

interface RoleRow {
  id: string;
  name: string;
  isSystem: boolean;
  userCount: number;
}

export function UsersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', roleId: '' });
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['users', search],
    queryFn: () => api<{ data: UserRow[]; total: number }>(`/users?pageSize=100&q=${encodeURIComponent(search)}`),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<{ data: RoleRow[] }>('/roles'),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api<{ data: { id: string }; temporaryPassword: string }>('/users', {
        method: 'POST',
        body: { name: form.name.trim(), email: form.email.trim(), roleId: form.roleId },
      }),
    onSuccess: (result) => {
      setError(null);
      setAdding(false);
      setForm({ name: '', email: '', roleId: '' });
      setNotice(
        `User created. Temporary password: ${result.temporaryPassword} — give it to them now; it is not shown again.`,
      );
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create the user.'),
  });

  const setStatus = useMutation({
    mutationFn: (row: UserRow) =>
      api(`/users/${row.id}`, {
        method: 'PATCH',
        body: { status: row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' },
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change the user.'),
  });

  const reset = useMutation({
    mutationFn: (row: UserRow) =>
      api<{ message: string }>(`/users/${row.id}/reset-password`, {
        method: 'POST',
        body: { newPassword: resetPassword },
      }),
    onSuccess: (result) => {
      setError(null);
      setResetTarget(null);
      setResetPassword('');
      setNotice(result.message);
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not reset the password.'),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Users</h1>
          <p className="text-sm text-slate-500">
            Who may sign in, and what role they hold. Users are disabled, never deleted.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" className="h-4 w-4" /> Add user
        </Button>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      <Card>
        <input
          className={`${inputClass} mb-4 max-w-xs`}
          placeholder="Search name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {users.isLoading && <Skeleton className="h-40 w-full" />}
        {users.data?.data.length === 0 && <EmptyState title="No users match that search." />}

        {(users.data?.data.length ?? 0) > 0 && (
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Last sign-in</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {users.data?.data.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <p className="font-medium">{row.name}</p>
                    <p className="text-xs text-slate-500">{row.email}</p>
                  </Td>
                  <Td>{row.roleName}</Td>
                  <Td>{row.lastLoginAt ? dateTime(row.lastLoginAt) : 'Never'}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <StatusChip tone={row.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                        {row.status === 'ACTIVE' ? 'Active' : 'Disabled'}
                      </StatusChip>
                      {row.mustResetPassword && (
                        <StatusChip tone="warning">Password reset due</StatusChip>
                      )}
                      {row.lockedUntil && new Date(row.lockedUntil) > new Date() && (
                        <StatusChip tone="critical">Locked</StatusChip>
                      )}
                    </div>
                  </Td>
                  <Td align="right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setResetTarget(row);
                          setResetPassword('');
                        }}
                      >
                        <KeyRound aria-hidden="true" className="h-3.5 w-3.5" /> Reset password
                      </Button>
                      <Button
                        size="sm"
                        variant={row.status === 'ACTIVE' ? 'secondary' : 'primary'}
                        onClick={() => setStatus.mutate(row)}
                      >
                        {row.status === 'ACTIVE' ? (
                          <>
                            <UserX aria-hidden="true" className="h-3.5 w-3.5" /> Disable
                          </>
                        ) : (
                          <>
                            <UserCheck aria-hidden="true" className="h-3.5 w-3.5" /> Enable
                          </>
                        )}
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {adding && (
        <Modal
          title="Add user"
          description="They receive a temporary password and must change it at first sign-in."
          onClose={() => setAdding(false)}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Full name" required>
              <input
                className={inputClass}
                required
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label="Email address" required>
              <input
                className={inputClass}
                type="email"
                required
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </Field>
            <Field label="Role" required>
              <select
                className={inputClass}
                required
                value={form.roleId}
                onChange={(event) => setForm({ ...form, roleId: event.target.value })}
              >
                <option value="">Choose a role</option>
                {roles.data?.data.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex gap-2">
              <Button type="submit" busy={create.isPending}>
                Create user
              </Button>
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {resetTarget && (
        <Modal
          title={`Reset password for ${resetTarget.name}`}
          description="They must change it again at their next sign-in. Every open session ends."
          onClose={() => setResetTarget(null)}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              reset.mutate(resetTarget);
            }}
          >
            <Field label="Temporary password" hint="At least 10 characters." required>
              <input
                className={inputClass}
                required
                minLength={10}
                value={resetPassword}
                onChange={(event) => setResetPassword(event.target.value)}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" busy={reset.isPending}>
                Reset password
              </Button>
              <Button type="button" variant="secondary" onClick={() => setResetTarget(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
